import React, { useState, useMemo } from "react";
import { Layers, Check } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { Modal } from '@ui';

/**
 * 组图生成选择弹窗
 * 用户勾选 2-12 个分镜，确认后生成角色一致的连续图片
 */
const SequentialImageModal = ({ isOpen, onClose, onConfirm, shots }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [selected, setSelected] = useState(new Set());

  const toggle = (index) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else if (next.size < 12) next.add(index);
      return next;
    });
  };

  const selectAll = () => {
    const indices = shots.map((_, i) => i).slice(0, 12);
    setSelected(new Set(indices));
  };

  const clearAll = () => setSelected(new Set());

  const sortedSelected = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected]);
  const canConfirm = selected.size >= 2 && selected.size <= 12;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(sortedSelected);
    setSelected(new Set());
  };

  const handleClose = () => {
    onClose();
    setSelected(new Set());
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={
      <span className="flex items-center gap-2">
        <Layers style={{ color: theme.colors.accentPrimary }} size={20} />
        组图生成 - 选择分镜
      </span>
    } width="max-w-[640px]">
      <div className="flex flex-col gap-3">
        {/* 说明 */}
        <div className="text-xs leading-relaxed px-1" style={{ color: 'var(--color-text-secondary)' }}>
          选择 2-12 个分镜，将一次生成角色一致的连续图片。已选 <span style={{ color: theme.colors.accentPrimary, fontWeight: 700 }}>{selected.size}</span> / 12
        </div>

        {/* 快捷操作 */}
        <div className="flex gap-2 px-1">
          <button onClick={selectAll} className="text-xs px-2 py-1 rounded cursor-pointer"
            style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
            全选(前12)
          </button>
          <button onClick={clearAll} className="text-xs px-2 py-1 rounded cursor-pointer"
            style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
            清空
          </button>
        </div>

        {/* 分镜列表 */}
        <div className="max-h-[400px] overflow-y-auto rounded-lg border" style={{ borderColor: 'var(--color-border-primary)' }}>
          {shots.map((shot, index) => {
            const isSelected = selected.has(index);
            const prompt = shot.image_prompt || shot.visual_content || shot.action || "";
            const preview = prompt.length > 60 ? prompt.substring(0, 60) + "..." : prompt;
            const disabled = !isSelected && selected.size >= 12;

            return (
              <div
                key={shot.id || index}
                onClick={() => !disabled && toggle(index)}
                className="flex items-center gap-3 px-3 py-2.5 transition-all duration-150 border-b last:border-b-0"
                style={{
                  borderColor: 'var(--color-border-primary)',
                  backgroundColor: isSelected ? `${theme.colors.accentPrimary}08` : 'transparent',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.4 : 1,
                }}
              >
                {/* 勾选框 */}
                <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border"
                  style={{
                    borderColor: isSelected ? theme.colors.accentPrimary : 'var(--color-border-secondary)',
                    backgroundColor: isSelected ? theme.colors.accentPrimary : 'transparent',
                  }}>
                  {isSelected && <Check size={12} color="white" strokeWidth={3} />}
                </div>

                {/* 缩略图 */}
                <div className="w-12 h-8 rounded overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                  {shot.generated_image ? (
                    <img src={shot.generated_image} className="w-full h-full object-cover" alt="" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xs"
                      style={{ color: 'var(--color-text-muted)' }}>无图</div>
                  )}
                </div>

                {/* 镜号 + prompt */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      #{shot.shot_number || shot.shotNumber || index + 1}
                    </span>
                    <span className="text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>
                      {preview || "（无描述）"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 底部按钮 */}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={handleClose}
            className="px-4 py-2 text-xs rounded-lg cursor-pointer"
            style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
            取消
          </button>
          <button onClick={handleConfirm} disabled={!canConfirm}
            className="px-4 py-2 text-xs font-bold rounded-lg cursor-pointer disabled:opacity-40"
            style={{
              backgroundColor: canConfirm ? theme.colors.accentPrimary : 'var(--color-bg-tertiary)',
              color: canConfirm ? 'white' : 'var(--color-text-muted)',
            }}>
            生成 {selected.size} 张组图
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SequentialImageModal;
